import { Amplify } from 'aws-amplify';
import { signOut as cognitoSignOut } from 'aws-amplify/auth';
import { signIn } from 'aws-amplify/auth';
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import type { AuthProvider } from '@aws-amplify/auth/dist/esm/types/inputs';
import { getAppStage } from 'src/utils/app';

let isAmplifyInitialized = false;

export const initializeAmplify = () => {
  const stage = getAppStage();
  const graphqlEndpoint =
    stage === 'prod'
      ? 'https://2jcdzattqbg7hb4s2no5dukny4.appsync-api.us-west-2.amazonaws.com/graphql'
      : 'https://kv6s3v5xhzeevaaambfhx6jw3e.appsync-api.us-west-2.amazonaws.com/graphql';
  const eventsEndpoint =
    stage === 'prod'
      ? 'https://z4ygoftixvbbbpyd2dudgwqrga.appsync-api.us-west-2.amazonaws.com/event'
      : 'https://uovpjgiplzbafogcs5gdztpbwm.appsync-api.us-west-2.amazonaws.com/event';

  try {
    if (!isAmplifyInitialized) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: 'us-west-2_op1uPQUYe',
            userPoolClientId: '5ktrvofvdidmkmoku14jmkff81',
            signUpVerificationMethod: 'code',
            identityPoolId: 'us-west-2:2da8aef8-aaa8-4277-8c0d-738fe1d897b8',
            loginWith: {
              oauth: {
                domain: 'stackline-enterprise-sso-signin.auth.us-west-2.amazoncognito.com',
                scopes: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
                redirectSignIn: [`${origin}/sign_in`],
                redirectSignOut: [`${origin}/sign_in`],
                responseType: 'code'
              }
            }
          }
        },
        API: {
          GraphQL: {
            endpoint: graphqlEndpoint,
            region: 'us-west-2',
            defaultAuthMode: 'lambda'
          },
          Events: {
            endpoint: eventsEndpoint,
            region: 'us-west-2',
            defaultAuthMode: 'lambda'
          }
        }
      });
      isAmplifyInitialized = true;
    }
  } catch (e) {
    console.warn(e);
  }
};

export const amplifySignOut = async () => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
  }
  try {
    await cognitoSignOut();
    return true;
  } catch (e) {
    console.error('Error with sign out', e);
    return false;
  }
};

/**
 * Safely fetches the current auth session with proper initialization and error handling
 * @returns The auth session if successful, null if there was an error
 */
export const safelyFetchAuthSession = async () => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
    console.log('Amplify config:', Amplify.getConfig());
  }

  try {
    const session = await fetchAuthSession();
    return session;
  } catch (error) {
    console.error('Failed to fetch auth session:', error);
    return null;
  }
};

export const amplifySignIn = async (email: string, password: string) => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
  }
  try {
    const { isSignedIn } = await signIn({ username: email, password });
    return isSignedIn;
  } catch (e) {
    console.error('Error signing in', e);
    return false;
  }
};

export const amplifyFederatedSignIn = async (provider: AuthProvider | { custom: string }, customState: string) => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
    console.log('Amplify config:', Amplify.getConfig());
  }
  try {
    // If provider is a string, convert it to the proper format
    const authProvider = typeof provider === 'string' ? { custom: provider } : provider;

    // Add identity_provider to the custom state
    const stateWithProvider = {
      ...JSON.parse(customState),
      identity_provider: authProvider.custom
    };

    await signInWithRedirect({
      provider: authProvider,
      customState: JSON.stringify(stateWithProvider)
    });
    return true;
  } catch (e) {
    console.error('Error with federated sign in', e);
    return false;
  }
};
